import React, { useState, useCallback } from "react";
import { Formik, Field } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";
import _get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/lib/Shared/Button";
import { downloadBusinessDocumentsAsync } from "state/slices/company";
import { useToast } from "hooks/useToast";
import { handleFilesUpload } from "utils/filesUtils";
import { createUploadFilePayload } from "utils/transactionRequestHandler";
import { uploadTemplateFileAsync } from "state/slices/templateFile";
import { updateTransaction } from "state/slices/transaction";

/**
 * SignatureDocument component handles the display, upload, and download of
 * unsigned and signed approval documents for various transaction types.
 * It supports both view and edit modes.
 *
 * Props:
 * - selectedTab: The current tab (e.g., Director, Shareholder)
 * - document: The document object for the current tab
 * - documentType: The type of the unsigned document
 * - signedDocumentType: The type of the signed document
 * - isViewMode: Boolean for view-only mode
 * - fileUploads: Uploaded files object
 */
const SignatureDocument = ({
  selectedTab,
  document,
  documentType,
  signedDocumentType,
  isViewMode,
  fileUploads,
}) => {
  const dispatch = useDispatch();
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { approvalDocs, transactions } = transactionUnderReview.items;
  // Find the index and name of the current approval document
  const officialIndex = approvalDocs.findIndex(
    (doc) => Object.values(doc)[0]._id === document._id
  );
  const documentName = Object.keys(approvalDocs[officialIndex]);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  /**
   * Handles uploading signed approval documents and updating the transaction.
   * Modularized for clarity and reuse.
   */
  const updateSignedApprovalDocs = useCallback(
    async (values) => {
      try {
        // Upload signed PDF files
        const uploadedFiles = await handleFilesUpload(values.fileUploads);
        let callFor = "";
        let formDetails = { companyId: "" };
        let fileName = "";
        // Determine which type of approval is being uploaded
        if (uploadedFiles?.ownConsentResolutionForDirectorApproval) {
          callFor = "directorApproval";
          fileName = uploadedFiles.ownConsentResolutionForDirectorApproval;
          formDetails = {
            ...formDetails,
            ownConsentResolutionForDirectorApproval: fileName,
          };
        }
        if (uploadedFiles?.ownConsentResolutionForShareholderApproval) {
          callFor = "shareholderApproval";
          fileName = uploadedFiles.ownConsentResolutionForShareholderApproval;
          formDetails = {
            ...formDetails,
            ownConsentResolutionForShareholderApproval: fileName,
          };
        }
        // Create template details
        const uploadFilePayload = createUploadFilePayload(
          formDetails.companyId,
          formDetails,
          null,
          fileName,
          callFor,
          transactions[0]._id
        );
        // Upload template file
        const fileUploadResponse = await dispatch(
          uploadTemplateFileAsync({ data: uploadFilePayload })
        );
        // Prepare update data for transaction
        let updateData = {};
        if (uploadFilePayload?.ownConsentResolutionForDirectorApproval) {
          updateData.directorApprovalDocs =
            fileUploadResponse.payload.document._id;
        }
        if (uploadFilePayload?.ownConsentResolutionForShareholderApproval) {
          updateData.shareholderApprovalDocs =
            fileUploadResponse.payload.document._id;
        }
        // Update transaction with new approval docs
        await dispatch(
          updateTransaction({
            trxId: transactions[0]._id,
            data: updateData,
            queryParams: { updateRelatedTransactions: true },
          })
        );
        toast("success", "Signed document uploaded and transaction updated.");
      } catch (error) {
        toast("error", error?.message ?? "Failed to upload signed document.");
      }
    },
    [dispatch, transactions, toast]
  );

  /**
   * Handles downloading unsigned business documents for the current approval type.
   */
  const handleDownloadDocuments = useCallback(async () => {
    try {
      setIsDownloading(true);
      const businessDocuments = [];
      getChangedDocumentsModuleData(documentType).forEach(
        (businessDocument) => {
          const fileKey = fileUploads[businessDocument.name];
          if (fileKey) {
            businessDocuments.push({
              key: fileKey,
              name: `${businessDocument.label}.${fileKey.split(".").pop()}`,
            });
          }
        }
      );
      await Promise.all(
        businessDocuments.map(async (businessDocument) =>
          dispatch(
            downloadBusinessDocumentsAsync({
              companyId: 1, // TODO: Replace with actual companyId if available
              businessDocument,
            })
          )
        )
      );
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong, please try again"
      );
    } finally {
      setIsDownloading(false);
    }
  }, [dispatch, documentType, fileUploads, toast]);

  return (
    <div className="py-4 mt-2 flex gap-5">
      {/* Unsigned document section */}
      <div className="w-full">
        <div className="px-2">Unsigned version</div>
        <div className="p-3 mt-2 bg-white">
          <Formik
            enableReinitialize
            initialValues={{
              fileUploads: {
                [documentType]: document?.convertedPdf,
              },
            }}
            onSubmit={() => null}
          >
            {() => (
              <div className="pl-4">
                <Field
                  name="fileUploads"
                  component={FileUploadForm}
                  documentData={getChangedDocumentsModuleData(documentType)}
                  resolvedFields={_get(document, "resolvedFields")}
                  onlyPreview
                  showDocumentLabel
                  editorKey={document.fileName}
                  documentName={documentType}
                />
              </div>
            )}
          </Formik>
        </div>
        <div className="mt-4">
          <Button
            className="ml-auto"
            onClick={handleDownloadDocuments}
            isLoading={isDownloading}
            loadingText="Downloading..."
          >
            Download unsigned versions
          </Button>
        </div>
      </div>
      {/* Signed document upload section (hidden in view mode) */}
      {document?.signedPdf && (
        <div className="w-full">
          <div className="px-2">Signed version</div>
          <div className="p-3 mt-2 bg-white">
            <Formik
              enableReinitialize
              initialValues={{
                fileUploads: {
                  [signedDocumentType]: document?.signedPdf,
                },
              }}
              onSubmit={updateSignedApprovalDocs}
            >
              {() => (
                <div className="pl-4">
                  <Field
                    name="fileUploads"
                    component={FileUploadForm}
                    documentData={getChangedDocumentsModuleData(
                      signedDocumentType
                    )}
                    resolvedFields={_get(document, "resolvedFields")}
                    onlyPreview={false}
                    showDocumentLabel
                    editorKey={document.fileName}
                    documentName={documentType}
                  />
                </div>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureDocument;
